import _ from 'lodash'
import { Module } from 'vuex'
import { RootState } from '@ps/types/store'
import qc from 'qc'
import {
  MapVideoNodeUUIDType,
  VideosManagerScript,
} from '@ps/bridge/qc/scripts/video-manager'
import * as videosManagerScript from '@ps/bridge/qc/scripts/video-manager'
import { checkIsSupportMP4, VideoScript } from '@ps/bridge/qc/scripts/video'
import store from '@ps/store'
import * as videoScript from '@ps/bridge/qc/scripts/video'
import filepath from '@ps/bridge/path'
import {
  addVideoToProject,
  cutVideoAndAddToProject,
  VideoToProjectParams,
} from '@ps/api/editor/video'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  executeAction,
  getAddVideoAction,
  getSetVideoAttrs,
} from '@ps/workbench/undo-redo'
import getResponsiveBox from '@com/utils/get-responsive-box.js'
import { getNodeByUUID } from '@ps/utils/game'
import { isVideo } from '@ps/bridge/qc/base'
import { Event, EventBus } from '@ps/utils/event-bus'
import { Message, MessageBox } from 'element-ui'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import nodePool from '@ps/bridge/qc/node-pool'
import {
  getVideoConfigByOriginPath,
  libDataPool,
} from '@ps/bridge/resource/pool'
import { setChildIndex } from '@ps/bridge/qc/node'
import { isIvPage } from '@ps/router/config'
import {
  addSegmentVideoToScene,
  updateSegmentVideoToScene,
  updateSegmentToScene,
} from '@ps/store/interactive-video/timeline/utiles/scene'
import {
  getSameVideoScene,
  setSyncStatusScene,
  syncSceneBySameVideo,
} from '@ps/store/interactive-video/brain/sync'
import { IvSyncStatus } from '@ps/store/interactive-video/brain/enums'
import i18n from '@ps/lang'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { getBrainItemByUuid } from '@ps/store/interactive-video/brain/brain'
export interface VideoState {
  videosManager: VideosManagerScript | null
  videos: {
    [key: string]: VideoScript
  }
  video: VideoScript | null
  toggleVideoChangeSize: boolean
}

// 允许编辑的 key
export type allowEditingKey =
  | 'sourceVideoUrl'
  | 'sourceVideoDuration'
  | 'videoUrl'
  | 'audioUrl'
  | 'playEndFrame'
  | 'loopNumber'
  | 'clipStartTime'
  | 'clipEndTime'
  | 'assetId'
  | 'autoPlay'
  | 'isMute'

export interface editParams {
  key: allowEditingKey
  value: number | boolean | string
  node: qc.Node | undefined
}

const getVideoMaxSize = (
  width: number,
  height: number,
): { width: number; height: number } => {
  const deviceWidth = gameIframe.UIRoot.width
  const deviceHeight = gameIframe.UIRoot.height
  const size = getResponsiveBox({
    maxWidth: deviceWidth,
    maxHeight: deviceHeight,
    width: width,
    height: height,
  })
  return {
    width: size.width,
    height: size.height,
  }
}

const videoModule: Module<VideoState, RootState> = {
  namespaced: true,

  state: {
    videos: {},
    videosManager: null,
    video: null,
    toggleVideoChangeSize: false, // 替换视频时是否改变视频大小
  },

  mutations: {
    // 设置当前 videoManagerBehavior 列表脚本
    setVideoManager(state) {
      // 如果已经添加了脚本，则不需要重复添加
      if (state.videosManager) return
      const gamePlay = store.getters['workspace/game/gamePlay']
      state.videosManager = videosManagerScript.getScript(
        gamePlay,
      ) as VideosManagerScript
    },

    setActiveVideo(state, node: qc.Node) {
      const targetNode = node || store.state.workspace.game.activeNode
      state.video = videoScript.getScript(targetNode) as VideoScript
    },

    // 获取当前视频节点的 videoScript 数据，并且赋值到 state 里
    setVideoScriptToState(state, node: qc.Node) {
      const targetNode = node || store.state.workspace.game.activeNode
      state.videos[node.uuid] = videoScript.getScript(targetNode) as VideoScript
    },

    // 设置视频层级
    setVideoZindex(state, { value, node }) {
      node.zIndex = value
    },
  },

  actions: {
    // 添加资源库的视频到项目中
    async addVideoResourceToProject(
      { rootState },
      params: Partial<VideoToProjectParams>,
    ) {
      const data: Partial<VideoToProjectParams> = {
        asset_id: String(params.asset_id),
        pid: Number(params.pid || rootState.workspace.pid),
        path_v: params.path_v || filepath.VIDEO_BASIC_PATH,
        path_a: params.path_a || filepath.AUDIO_BASIC_PATH,
        format: checkIsSupportMP4() ? 'mp4' : 'ts',
      }
      if (typeof params.from !== 'undefined') {
        data.from = Number(params.from)
        // 如果是个人资产库，需要传 path_origin
        if (params.from === 3) {
          data.path_origin = filepath.ORIGIN_RESOURCE_PATH
        }
      }
      const res = await addVideoToProject(data)
      if (_.get(res, 'code') === 0) {
        return res
      }
      const e = new Error(_.get(res, 'msg') || '添加资源库的视频失败')
      e.name = String(_.get(res, 'code'))
      throw e
    },

    async cutVideoToProject(
      { rootState, dispatch, state },
      params: {
        pid: number | undefined
        url: string
        stime: number
        etime: number
        path_v: string | undefined
        path_a: string | undefined
        record: boolean
        nodes: qc.Node[] | qc.Node
        isUpdateNode: boolean
      },
    ) {
      const data = {
        pid: Number(params.pid || rootState.workspace.pid),
        url: params.url,
        stime: params.stime,
        etime: params.etime,
        path_v: params.path_v || filepath.VIDEO_BASIC_PATH,
        path_a: params.path_a || filepath.AUDIO_BASIC_PATH,
        format: checkIsSupportMP4() ? 'mp4' : 'ts',
      }
      const res = await cutVideoAndAddToProject(data)
      if (_.get(res, 'code') === 0) {
        const videoUrl = _.get(res, 'data.video.path')
        const audioUrl = _.get(res, 'data.audio.path')
        const duration = _.get(res, 'data.video.duration')
        const size = _.get(res, 'data.video.file_size')
        // 支持只添加到项目，不更新接口
        if (!params.isUpdateNode) return

        const update = (attrs: {
          videoUrl: string
          audioUrl: string
          stime: number
          etime: number
          node: qc.Node
        }) => {
          const { videoUrl, audioUrl, stime, etime, node } = attrs
          dispatch('setVideoAttribute', {
            params: [
              {
                key: 'videoUrl',
                value: videoUrl,
                node,
              },
              {
                key: 'audioUrl',
                value: audioUrl,
                node,
              },
              {
                key: 'clipStartTime',
                value: stime,
                node,
              },
              {
                key: 'clipEndTime',
                value: etime,
                node,
              },
            ],
            record: false,
          })
        }
        const getNodes = () => {
          if (params.nodes && Array.isArray(params.nodes)) {
            return params.nodes
          } else if (params.nodes) {
            return [params.nodes]
          }
          return [store.state.workspace.game.activeNode]
        }
        const nodes = getNodes()
        const activeNode = store.state.workspace.game.activeNode
        if (activeNode && nodes.some((val) => activeNode.uuid === val.uuid)) {
          const currentVideo = state.videos[activeNode.uuid] as VideoScript
          currentVideo.videoLoaded = false
          dispatch('addVideoLoadListener', activeNode)
          store.commit('workspace/game/setFileSize', {
            value: size,
            node: activeNode,
          })
          store.commit('workspace/game/setDuration', {
            value: duration,
            node: activeNode,
          })
        }
        const newValueArr = nodes.map((node) => {
          return {
            videoUrl,
            audioUrl,
            stime: params.stime,
            etime: params.etime,
            node,
          }
        })
        const oldValueArr = nodes.map((node) => {
          const currentVideo = videoScript.getScript(node) as VideoScript
          return {
            videoUrl: currentVideo.videoUrl,
            audioUrl: currentVideo.audioUrl,
            stime: currentVideo.clipStartTime,
            etime: currentVideo.clipEndTime,
            node,
          }
        })
        // update libClip
        await store.dispatch('workspace/editorConfig/updateLibClip', {
          clipPath: nodes[0].Video.videoUrl,
          path: videoUrl,
        })
        await executeAction({
          action: {
            execute: ({ newValue }) => {
              newValue.forEach((val) => {
                update(val)
              })
              EventBus.$emit(Event.IV_BRAIN_CHANGE)
            },
            undo: ({ oldValue }) => {
              oldValue.forEach((val) => {
                update(val)
              })
              EventBus.$emit(Event.IV_BRAIN_CHANGE)
            },
            value: {
              newValue: newValueArr,
              oldValue: oldValueArr,
            },
            methodName: 'cutVideoToProjectAction',
          },
          // 视频更换历史记录暂时不开启，视频加载太慢，导致效果很差
          record: false,
        })
        return res
      }
      throw new Error(_.get(res, 'msg') || '裁剪视频入库失败')
    },

    // 新增视频 qc.dom 节点
    async addVideoNode(
      { rootState, dispatch },
      {
        assetId,
        name,
        width,
        height,
        videoUrl,
        audioUrl,
        sourceVideoUrl,
        videoCoverUrl,
        sourceVideoDuration,
        duration,
        size,
        record = true,
      },
    ) {
      const currentSceneNode =
        rootState.workspace.game.sceneManager.currentSceneNode
      // 互动视频编辑器添加视频转换替换逻辑
      if (isIvPage()) {
        const brainItem = getBrainItemByUuid(currentSceneNode.uuid)
        if (brainItem && brainItem.videoUuid) {
          const videoNode = nodePool.find(brainItem.videoUuid) as qc.Node
          dispatch('toggleVideoNode', {
            node: videoNode,
            assetId,
            name,
            width,
            height,
            videoUrl,
            audioUrl,
            sourceVideoUrl,
            videoCoverUrl,
            sourceVideoDuration,
            duration,
            size,
            record,
          })
          return
        }
      }
      const videoNode = gameIframe.qcGame.add.dom(currentSceneNode)
      // 视频节点放在最底下
      const videoList = currentSceneNode.children.filter((child: qc.Dom) =>
        isVideo(child),
      )
      const index = videoList.length === 0 ? 0 : videoList.length - 1
      setChildIndex({ node: videoNode, index })
      const videoSize = getVideoMaxSize(width, height)
      const videoScriptData = videoScript.getScript(videoNode)
      if (videoScriptData) {
        videoScriptData.$data.nativeSize = {
          ...videoScriptData.$data.nativeSize,
          width: videoSize.width,
          height: videoSize.height,
        }
      }
      // 设置视频节点在最底层
      videoNode.pos = gameIframe.qc.Dom.POS_BACK
      EventBus.$emit(Event.ADD_GAME_OBJECT, videoNode.uuid)
      await store.dispatch('workspace/game/updateActiveNode', {
        node: videoNode,
        record: false,
      })
      videoNode.name = name
      store.commit('workspace/game/setWidth', {
        value: videoSize.width,
        node: videoNode,
      })
      store.commit('workspace/game/setHeight', {
        value: videoSize.height,
        node: videoNode,
      })
      store.commit('workspace/game/setRatioLock', {
        value: true,
        node: videoNode,
      })
      store.commit('workspace/game/setFileSize', {
        value: size,
        node: videoNode,
      })
      store.commit('workspace/game/setDuration', {
        value: duration,
        node: videoNode,
      })
      dispatch('setVideoAttribute', {
        params: [
          {
            key: 'videoUrl',
            value: videoUrl,
          },
          {
            key: 'audioUrl',
            value: audioUrl,
          },
          {
            key: 'sourceVideoUrl',
            value: sourceVideoUrl,
          },
          {
            key: 'videoCoverUrl',
            value: videoCoverUrl,
          },
          {
            key: 'assetId',
            value: assetId,
          },
          {
            key: 'sourceVideoDuration',
            value: sourceVideoDuration,
          },
          {
            key: 'clipStartTime',
            value: 0,
          },
          {
            key: 'clipEndTime',
            value: sourceVideoDuration,
          },
        ],
        record: false,
      })

      // 记录到资源映射池，方便后续使用
      libDataPool.setLibData(videoUrl, {
        name: name,
        data: {
          width,
          height,
          sourceVideoUrl,
          videoCoverUrl,
          videoUrl,
          audioUrl,
          sourceVideoDuration,
          duration,
          size,
        },
      })

      dispatch('addVideoLoadListener', videoNode)

      const gameState = store.state.workspace.game
      let ivNodes, ivAst, ivNewNodes, ivNewAst
      if (isIvPage()) {
        const brainData = store.state.ivBrain.brainData || []
        const curData = brainData.find(
          (item) => item.sceneUuid === currentSceneNode.uuid,
        )
        store.dispatch('ivBrain/bindVideo', {
          sceneUuid: currentSceneNode.uuid,
          uuid: videoNode.uuid,
        })
        // 添加视频元件
        addSegmentVideoToScene(videoNode.uuid, currentSceneNode.uuid, curData)
        // 视频uuid作为视频轨道group_id
        updateSegmentToScene(currentSceneNode.uuid, videoNode.uuid, {
          groupId: videoNode.uuid,
        })

        EventBus.$emit(Event.IV_BRAIN_CHANGE)

        const sceneNodes = getSameVideoScene(currentSceneNode, [
          IvSyncStatus.Sync,
          IvSyncStatus.None,
        ])
        if (sceneNodes.length > 0) {
          await MessageBox.confirm(
            i18n.t('interactiveVideo.iv.sync.content'),
            i18n.t('interactiveVideo.iv.sync.title'),
            {
              confirmButtonText: i18n.t('interactiveVideo.iv.sync.sync'),
              cancelButtonText: i18n.t('interactiveVideo.iv.sync.noSync'),
              type: 'warning',
              customClass: 'pt-custom-confirm-message',
              center: true,
            },
          )
            .then(() => {
              console.log(11111)
              setSyncStatusScene(currentSceneNode, IvSyncStatus.Sync)
              const res = syncSceneBySameVideo(sceneNodes[0], currentSceneNode)
              ivNodes = res.nodes
              ivAst = res.ast
              ivNewNodes = res.newNodes
              ivNewAst = res.newAst
              store.dispatch('ivTimeline/refreshTimeline')
            })
            .catch(() => {
              console.log(22222)
              setSyncStatusScene(currentSceneNode, IvSyncStatus.noSync)
            })
        }
      }

      executeAction({
        action: getAddVideoAction({
          newValue: {
            node: videoNode,
            parent: currentSceneNode,
            index: index,
            ivNewNodes,
            ivNewAst,
          },
          oldValue: {
            node: videoNode,
            activeUuid: gameState.activeNode ? gameState.activeNode.uuid : '',
            ivNodes: ivNodes,
            ivAst: ivAst,
          },
        }),
        record,
      })
    },

    // 切换视频节点内容
    async toggleVideoNode(
      { state, dispatch, rootState },
      {
        name,
        node,
        assetId,
        width,
        height,
        videoUrl,
        audioUrl,
        sourceVideoUrl,
        videoCoverUrl,
        sourceVideoDuration,
        duration,
        size,
        // 视频更换历史记录暂时不开启，视频加载太慢，导致效果很差
        record = true,
      },
    ) {
      const nodes = Array.isArray(node) ? node : [node]

      const taskList = nodes.map((node) => {
        const activeNode = node || store.state.workspace.game.activeNode
        if (!isVideo(activeNode)) {
          Message.info('当前没有选中节点或者选中的节点不是视频节点')
          return
        }
        const videoSize = getVideoMaxSize(width, height)
        const videoScriptData = videoScript.getScript(activeNode)
        if (videoScriptData) {
          videoScriptData.$data.nativeSize = {
            ...videoScriptData.$data.nativeSize,
            width: videoSize.width,
            height: videoSize.height,
          }
        }
        if (state.toggleVideoChangeSize) {
          store.commit('workspace/game/setWidth', {
            value: width,
            node: activeNode,
          })
          store.commit('workspace/game/setHeight', {
            value: height,
            node: activeNode,
          })
        }
        store.commit('workspace/game/setFileSize', {
          value: size,
          node: activeNode,
        })
        store.commit('workspace/game/setDuration', {
          value: duration,
          node: activeNode,
        })
        const currentVideo = videoScript.getScript(activeNode) as VideoScript
        currentVideo.videoLoaded = false
        dispatch('addVideoLoadListener', activeNode)
        return {
          node: activeNode,
          currentVideo,
        }
      })
      if (taskList.length === 0) return
      const newValueArr = taskList.map((task) => {
        return {
          videoUrl,
          audioUrl,
          assetId,
          sourceVideoUrl,
          stime: 0,
          etime: sourceVideoDuration,
          sourceVideoDuration,
          node: task?.node,
        }
      })
      const oldValueArr = taskList.map((task) => {
        return {
          videoUrl: task?.currentVideo?.videoUrl,
          audioUrl: task?.currentVideo?.audioUrl,
          stime: task?.currentVideo?.clipStartTime,
          etime: task?.currentVideo?.clipEndTime,
          sourceVideoDuration: task?.currentVideo?.sourceVideoDuration,
          assetId: task?.currentVideo?.assetId,
          node: task?.node,
          sourceVideoUrl: task?.currentVideo?.sourceVideoUrl,
        }
      })

      const update = (attrs: {
        videoUrl: string
        audioUrl: string
        sourceVideoUrl: string
        assetId: string
        sourceVideoDuration: number
        stime: number
        etime: number
        node: qc.Node
      }) => {
        const {
          videoUrl,
          audioUrl,
          sourceVideoUrl,
          assetId,
          stime,
          etime,
          sourceVideoDuration,
          node,
        } = attrs
        dispatch('setVideoAttribute', {
          params: [
            {
              key: 'videoUrl',
              value: videoUrl,
              node,
            },
            {
              key: 'audioUrl',
              value: audioUrl,
              node,
            },
            {
              key: 'sourceVideoUrl',
              value: sourceVideoUrl,
              node,
            },
            {
              key: 'videoCoverUrl',
              value: videoCoverUrl,
              node,
            },
            {
              key: 'assetId',
              value: assetId,
              node,
            },
            {
              key: 'sourceVideoDuration',
              value: sourceVideoDuration,
              node,
            },
            {
              key: 'clipStartTime',
              value: stime,
              node,
            },
            {
              key: 'clipEndTime',
              value: etime,
              node,
            },
          ],
          record: false,
        })

        if (isIvPage()) {
          const brainData = store.state.ivBrain.brainData || []
          const curBrainData = brainData.find(
            (item) => item.videoUuid === node.uuid,
          )
          if (curBrainData) {
            updateSegmentVideoToScene(
              node.uuid,
              curBrainData.sceneUuid,
              curBrainData.link,
            )
            store.dispatch('ivTimeline/refreshTimeline')
          }
        }
      }

      // 记录到资源映射池，方便后续使用
      libDataPool.setLibData(videoUrl, {
        name: name,
        data: {
          width,
          height,
          sourceVideoUrl,
          videoCoverUrl,
          videoUrl,
          audioUrl,
          sourceVideoDuration,
          duration,
          size,
        },
      })
      let isSync = false
      // 互动视频判断是否有场景需要同步
      if (isIvPage()) {
        const currentSceneNode =
          rootState.workspace.game.sceneManager.currentSceneNode

        // 替换视频时，如果原视频只剩下一个相同视频的同步场景，需要把那个场景弄成初始化
        const oriSameSceneNodes = getSameVideoScene(
          currentSceneNode,
          [IvSyncStatus.Sync],
          true,
          oldValueArr[0].sourceVideoUrl,
        )
        if (oriSameSceneNodes.length === 1) {
          setSyncStatusScene(oriSameSceneNodes[0], IvSyncStatus.None)
        }

        const sceneNodes = getSameVideoScene(
          currentSceneNode,
          [IvSyncStatus.Sync, IvSyncStatus.None],
          true,
          sourceVideoUrl,
        )

        if (sceneNodes.length > 0) {
          await MessageBox.confirm(
            i18n.t('interactiveVideo.iv.sync.content'),
            i18n.t('interactiveVideo.iv.sync.title'),
            {
              confirmButtonText: i18n.t('interactiveVideo.iv.sync.sync'),
              cancelButtonText: i18n.t('interactiveVideo.iv.sync.noSync'),
              type: 'warning',
              customClass: 'pt-custom-confirm-message',
              center: true,
            },
          )
            .then(() => {
              console.log(11111)
              setSyncStatusScene(currentSceneNode, IvSyncStatus.Sync)
              const res = syncSceneBySameVideo(sceneNodes[0], currentSceneNode)
              store.dispatch('ivTimeline/refreshTimeline')
              isSync = true
              oldValueArr[0].ivNodes = res.nodes
              oldValueArr[0].ivAst = res.ast
              newValueArr[0].ivNewNodes = res.newNodes
              newValueArr[0].ivNewAst = res.newAst
            })
            .catch(() => {
              console.log(22222)
              setSyncStatusScene(currentSceneNode, IvSyncStatus.noSync)
              isSync = false
            })
        } else {
          isSync = false
          setSyncStatusScene(currentSceneNode, IvSyncStatus.None)
        }
      }
      await executeAction({
        action: {
          execute: ({ newValue }) => {
            newValue.forEach((item) => {
              if (isIvPage() && isSync) {
                // 互动视频场景同步
                const ivNewNodes = item.ivNewNodes
                const ivNewAst = item.ivNewAst
                if (ivNewNodes) {
                  const currentSceneNode =
                    store.state.workspace.sceneManager.currentSceneNode
                  currentSceneNode.removeChildren()
                  ivNewNodes.forEach((node) => {
                    currentSceneNode.addChild(node)
                  })
                  const ped = editorScript.getScript(
                    currentSceneNode,
                  ) as EditorDataScript
                  ped.vpAst = _.cloneDeep(ivNewAst)
                }
              } else {
                update(item)
              }
            })
            EventBus.$emit(Event.IV_BRAIN_CHANGE)
          },
          undo: ({ oldValue }) => {
            oldValue.forEach((item) => {
              if (isIvPage() && isSync) {
                const ivNodes = item.ivNodes
                const ivAst = item.ivAst
                if (ivNodes) {
                  const currentSceneNode =
                    store.state.workspace.sceneManager.currentSceneNode
                  currentSceneNode.removeChildren()
                  ivNodes.forEach((node) => {
                    currentSceneNode.addChild(node)
                  })
                  const ped = editorScript.getScript(
                    currentSceneNode,
                  ) as EditorDataScript
                  ped.vpAst = _.cloneDeep(ivAst)
                }
              } else {
                update(item)
              }
            })
            EventBus.$emit(Event.IV_BRAIN_CHANGE)
          },
          value: {
            newValue: newValueArr,
            oldValue: oldValueArr,
          },
          methodName: 'toggleVideoNodeAction',
        },
        record,
      })
    },

    deleteVideo({ state, commit }, { sceneNode, videoNode }) {
      const sceneUUID = sceneNode.uuid
      const videoNodeUUID = videoNode.uuid
      commit('setVideoManager')
      if (state.videosManager) {
        state.videosManager.deleteVideoNode(sceneUUID, videoNodeUUID)
      }
    },

    deleteSceneVideo({ state, commit }, { sceneNode }) {
      const sceneUUID = sceneNode.uuid
      commit('setVideoManager')
      if (state.videosManager) {
        state.videosManager.deleteAllVideoNode(sceneUUID)
      }
    },

    playVideo({ state }, isReplay: boolean) {
      const activeNode = store.state.workspace.game.activeNode
      const videoBeh = state.videos[activeNode.uuid] as VideoScript
      videoBeh.play(isReplay)
    },

    stopVideo({ state }, node) {
      const activeNode = node || store.state.workspace.game.activeNode
      const videoBeh = state.videos[activeNode.uuid] as VideoScript
      videoBeh.stop()
    },

    pauseVideo({ state }, node) {
      const activeNode = node || store.state.workspace.game.activeNode
      const videoBeh = state.videos[activeNode.uuid] as VideoScript
      videoBeh.pause()
    },

    async getUpperLimitStatus() {
      // 场景视频限制数量控制
      const videoLimitNum = 4
      const currentSceneTree = store.getters['workspace/game/currentSceneTree']
      const currentSceneNode =
        store.state.workspace.game.sceneManager.currentSceneNode
      const videoNodes = currentSceneTree.filter((item: qc.Node) => {
        const node = getNodeByUUID({
          item: currentSceneNode,
          id: item.uuid,
        })
        return isVideo(node)
      })
      // 如果当前场景包含 4 个视频节点，则不允许添加新的视频节点
      return videoNodes.length >= videoLimitNum
    },

    setVideosListZindex({ commit }, { nodeList, oldNodeList, record = true }) {
      executeAction({
        action: {
          execute: ({ newValue }) => {
            const { nodes } = newValue
            // 因为视频节点都是在 底层 Dom.POS_BACK，导致设置正数的 zindex 会被引擎改为 -1 ，因此需要使用负数
            nodes.forEach((node: qc.Node, index: number) => {
              commit('setVideoZindex', {
                node,
                value: -1 - index,
              })
            })
          },
          undo: ({ oldValue }) => {
            const { nodes } = oldValue
            // 因为视频节点都是在 底层 Dom.POS_BACK，导致设置正数的 zindex 会被引擎改为 -1 ，因此需要使用负数
            nodes.forEach((node: qc.Node, index: number) => {
              commit('setVideoZindex', {
                node,
                value: -1 - index,
              })
            })
          },
          value: {
            newValue: {
              nodes: nodeList,
            },
            oldValue: {
              nodes: oldNodeList,
            },
          },
          methodName: 'setVideosListZindexAction',
        },
        record,
      })
    },

    // 设置视频属性
    setVideoAttribute(
      state,
      {
        params,
        record = true,
      }: {
        params: editParams | editParams[]
        record: boolean
      },
    ) {
      if (!_.isArray(params)) {
        params = [params]
      }

      executeAction({
        action: getSetVideoAttrs({
          newValue: {
            attrs: params.map((video) => {
              const node = video.node || store.state.workspace.game.activeNode
              return {
                ...video,
                node,
              }
            }),
          },
          oldValue: {
            attrs: params.map((video) => {
              const node = video.node || store.state.workspace.game.activeNode
              return {
                ...video,
                value: videoScript.getScript(node)?.[video.key],
                node,
              }
            }),
          },
        }),
        record,
      })
    },

    setVideosManager({ rootState, state, commit }) {
      commit('setVideoManager')
      const sceneList = rootState.workspace.sceneManager.sceneList
      if (state.videosManager) {
        state.videosManager.mapVideoNodes = {}
      } else {
        console.error('videosManager is null')
      }
      // 遍历所有场景，赋值所有的视频节点到 mapVideoNodes 里面
      sceneList.forEach((scene: qc.Node) => {
        const sceneUUID = scene.uuid
        const MapVideoNodeUUIDType: MapVideoNodeUUIDType = {}
        getDescendants(scene, false).forEach((node: qc.Node) => {
          if (isVideo(node)) {
            MapVideoNodeUUIDType[node.uuid] = node.uuid
          }
        })
        if (!_.isEmpty(MapVideoNodeUUIDType)) {
          if (state.videosManager) {
            state.videosManager.mapVideoNodes[sceneUUID] = MapVideoNodeUUIDType
          }
        }
      })
    },

    addVideoLoadListener(state, node: qc.Node) {
      const video = videoScript.getScript(node)
      if (video && !video.videoLoaded) {
        console.log('锁定操作')
        store.commit('workspace/lockPage')
        video.eventDisp.addOnce(gameIframe.ps.VideoEvent.LOAD_COMPLETE, () => {
          console.log('解锁操作')
          store.commit('workspace/unlockPage')
          // 截取视频资源画面
          EventBus.$emit(Event.CAPTUREGAME)
        })
      }
    },
  },
}

export default videoModule
